import React, { useMemo } from "react";
import PropTypes from "prop-types";
import TableCard from "../components/ui-components/TableCard";
import FlexTable from "../components/ui-components/FlexTable";
import EmptyState from "../components/ui-components/EmptyState";
import Badge from "../components/ui-components/Badge";

// VerificationReport renders internal/provision.Report: the API's own
// read-back of the tenant's GCP resources, one row per check.
//
// Every failing check is shown, not just the first. A provisioning run that
// created the dataset and the bucket but failed the tokenCreator binding is
// a specific, recoverable state, and an operator who sees only "verification
// failed" has to go find out which of eight things broke.
//
// Want and Got are rendered verbatim and monospaced. They are IAM roles,
// members and locations; abbreviating them would remove exactly the
// characters that tell two service accounts apart.
const check = (row) => ({ ...row, id: row.name });

// TableCard renders `errorMessage` and nothing else, so anything the server
// said has to be composed here or it is lost. That loss is not theoretical:
// an auto-verify that fired into a deploy window showed the same four words
// a genuine GCP read failure would have, and finding the difference took a
// trip through Cloud Logging.
//
// Three cases, because they call for three different actions:
//   - no status at all: the request never got an HTTP reply. Wait.
//   - a status but no message: nginx answered, not the API. Wait.
//   - a message: the API's own words. Read them.
const describeError = (error) => {
	if (!error) return null;

	const detail = error.serverBody?.error?.message || null;
	const status = error.status;

	let hint;
	if (!status) {
		hint = "The API did not respond. It is unreachable for a few minutes during a deploy — retry shortly.";
	} else if (!detail) {
		hint = `The API replied ${status} with no message, which usually means it is restarting after a deploy rather than that anything is wrong with this tenant.`;
	} else {
		hint = detail;
	}

	return (
		<>
			<div>Could not verify</div>
			<div className="mt-1 max-w-[460px] text-center text-[12px] opacity-80">{hint}</div>
		</>
	);
};

// `title` and `source` exist because there are now two reports on the tenant
// page and they check different planes: the data plane (dataset, bucket,
// service account, connection) and the identity plane (realm, clients,
// audience mapper, domain mapping, DNS). Two cards both headed "Verification"
// and both claiming to read GCP back would let an operator read a failing DNS
// row as a broken dataset. Defaults keep the M3a card's wording byte-for-byte.
export default function VerificationReport({ report, onRerun, isLoading, error, title, source, subject }) {
	const rows = useMemo(() => (report?.checks || []).map(check), [report]);

	const columns = useMemo(
		() => [
			{ Header: "Check", accessor: "name", width: 240 },
			{
				Header: "Result",
				accessor: "ok",
				width: 100,
				// Three states, not two. An advisory check that is not satisfied
				// is "Pending", in the neutral tone: it describes something that
				// has not happened yet rather than something that is wrong, and
				// it does not stop the tenant being activated. Rendering it as
				// "Fail" -- which is what this column did before readiness grew
				// advisory rows -- puts a red badge next to "this tenant has not
				// imported any billing data yet", which reads as a broken tenant
				// and sends the operator looking for a fault there is none of.
				Cell: ({ row, value }) => {
					if (value) return <Badge status="success" label="Pass" size="sm" />;
					if (row.original.advisory) return <Badge status="pending" label="Pending" size="sm" />;
					return <Badge status="failed" label="Fail" size="sm" />;
				},
			},
			{
				Header: "Expected",
				accessor: "want",
				width: 280,
				Cell: ({ value }) => <span className="break-all font-mono text-[12px]">{value}</span>,
			},
			{
				Header: "Found",
				accessor: "got",
				width: 280,
				Cell: ({ row, value }) => {
					const tone = row.original.ok || row.original.advisory ? "" : "text-danger";
					return <span className={`break-all font-mono text-[12px] ${tone}`}>{value}</span>;
				},
			},
		],
		[]
	);

	// A report with ok:true and no checks would be a vacuous pass, so the
	// summary counts rather than trusting the flag alone.
	//
	// Advisory rows are counted separately and worded separately. "2 of 10
	// checks fail. Fix the cause and provision again" is wrong twice over when
	// both of those rows say the tenant has no billing data yet: provisioning
	// again would not change them, and nothing is waiting on them.
	const failed = rows.filter((r) => !r.ok && !r.advisory).length;
	const pending = rows.filter((r) => !r.ok && r.advisory).length;

	const pendingNote = pending === 0
		? ""
		: ` ${pending} ${pending === 1 ? "check is" : "checks are"} pending: they describe data the tenant has not imported yet and do not hold anything up.`;

	const subtitle = rows.length === 0
		? `The API reads the tenant's resources back from ${source}; the job that created them does not report on itself.`
		: failed === 0
			? `${pending === 0 ? `All ${rows.length} checks pass.` : `${rows.length - pending} of ${rows.length} checks pass and nothing is blocking.`} These are read back from ${source}, not from the job's own report.${pendingNote}`
			: `${failed} of ${rows.length} checks fail. Fix the cause and provision again — the job is idempotent.${pendingNote}`;

	return (
		<TableCard
			title={title}
			subtitle={subtitle}
			isLoading={isLoading}
			error={error}
			errorMessage={describeError(error)}
			onRetry={onRerun}
			primaryAction={
				onRerun && {
					// The subject is in the label, not only in the card title,
					// because two cards on this page both offer verification and
					// a button whose accessible name is just "Verify" is
					// ambiguous to anyone not reading the heading above it.
					label: isLoading
						? "Verifying…"
						: rows.length === 0
							? `Verify${subject ? ` ${subject}` : ""}`
							: `Verify${subject ? ` ${subject}` : ""} again`,
					onClick: onRerun,
					disabled: isLoading,
				}
			}
		>
			{rows.length === 0 ? (
				<EmptyState title="Not verified yet" message="Run verification to read the resources back." />
			) : (
				<FlexTable columns={columns} data={rows} getRowId={(row) => row.id} />
			)}
		</TableCard>
	);
}

VerificationReport.propTypes = {
	report: PropTypes.shape({
		ok: PropTypes.bool,
		checks: PropTypes.arrayOf(
			PropTypes.shape({
				name: PropTypes.string,
				want: PropTypes.string,
				got: PropTypes.string,
				ok: PropTypes.bool,
				// Omitted by the API when false (`json:"advisory,omitempty"`),
				// so an old report renders exactly as it did before.
				advisory: PropTypes.bool,
			})
		),
	}),
	onRerun: PropTypes.func,
	isLoading: PropTypes.bool,
	error: PropTypes.any,
	title: PropTypes.string,
	source: PropTypes.string,
	subject: PropTypes.string,
};

VerificationReport.defaultProps = {
	title: "Verification",
	source: "GCP",
	subject: "",
};
